import { ALGOLIA_OBJECT_ID_ATTRIBUTE_KEY } from "@made-people/grease-algolia";
import {
  centraCheckoutApiProductToGreaseProductExtraAttributes,
  CentraCheckoutApiMappers,
} from "@made-people/grease-centra";
import { ProductBase } from "@made-people/grease-product";
import { z } from "zod";

export const mmColorSwatchSchema = z.object({
  image: z.object({
    url: z.string(),
  }),
  description: z.string(),
});

/**
 * Adds the product color image URL to the extra attributes of the product,
 * used to display the color swatch in the product details page.
 */
export const mmProductExtraAttributes: CentraCheckoutApiMappers["centraCheckoutApiProductToGreaseProductExtraAttributes"] =
  (product) => {
    const isFixedBundle = product.bundleInfo?.type === "fixed";

    const extraAttributes = [
      ...centraCheckoutApiProductToGreaseProductExtraAttributes(product),
      ...(isFixedBundle
        ? [
            {
              attributeKey: "is_fixed_bundle",
              attributeLabel: "Is Fixed Bundle",
              value: true,
              valueLabel: "true",
            },
          ]
        : []),
      {
        attributeKey: "product_parent_id",
        attributeLabel: "Product Parent ID",
        value: product.productSku,
        valueLabel: product.productSku,
      },
      {
        attributeKey: "isLightShaping",
        attributeLabel: "Is Light Shaping",
        value: product.productSku === "8475" ? "true" : "false",
        valueLabel: product.productSku === "8475" ? "true" : "false",
      },
      {
        attributeKey: "product_main_category",
        attributeLabel: "Product Main Category",
        value: product.categoryUri,
        valueLabel: product.categoryName?.join(" / ") ?? "Miss Mary of Sweden",
      },
      {
        attributeKey: "product_variant_name",
        attributeLabel: "Product Variant Name",
        value: product.variantName,
        valueLabel: product.variantName,
      },
      {
        attributeKey: ALGOLIA_OBJECT_ID_ATTRIBUTE_KEY,
        attributeLabel: ALGOLIA_OBJECT_ID_ATTRIBUTE_KEY,
        value: `product-${product.product}`,
        valueLabel: `product-${product.product}`,
      },
    ];

    const colorSwatchData = mmColorSwatchSchema.safeParse(product.color_swatch);
    if (colorSwatchData.success) {
      return [
        ...extraAttributes,
        {
          attributeKey: "product_color_image_url",
          attributeLabel: "Product Color Image URL",
          value: colorSwatchData.data.image.url,
          valueLabel: colorSwatchData.data.description,
        },
      ];
    }

    const colorData = mmColorSwatchSchema.safeParse(product.color);
    if (colorData.success) {
      return [
        ...extraAttributes,
        {
          attributeKey: "product_color_image_url",
          attributeLabel: "Product Color Image URL",
          value: colorData.data.image.url,
          valueLabel: colorData.data.description,
        },
      ];
    }

    return extraAttributes;
  };

export const mmProductIsFixedBundle = (product: ProductBase): boolean => {
  return (
    product.attributes.find((x) => x.attributeKey === "is_fixed_bundle")
      ?.value === true
  );
};
